import React from 'react';
import { Clock, MapPin, Phone, ExternalLink } from 'lucide-react';
import { businessInfo, openingHours } from '../data/mock';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

const HoursLocationSection = () => {
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });

  return (
    <section id="hours" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-amber-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Find Us
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Hours & Location
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Opening Hours */}
          <Card className="bg-white border-slate-200 overflow-hidden">
            <div className="bg-slate-900 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Opening Hours</h3>
                  <p className="text-slate-400 text-sm">When we're available</p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="space-y-3">
                {openingHours.map((item) => (
                  <div
                    key={item.day}
                    className={`flex items-center justify-between py-3 px-4 rounded-lg transition-colors ${
                      today === item.day
                        ? 'bg-amber-50 border border-amber-200'
                        : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className={`font-medium ${
                      today === item.day ? 'text-amber-700' : 'text-slate-700'
                    }`}>
                      {item.day}
                      {today === item.day && (
                        <span className="ml-2 text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full">
                          Today
                        </span>
                      )}
                    </span>
                    <span className={`${
                      today === item.day ? 'text-amber-600 font-semibold' : 'text-slate-600'
                    }`}>
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-slate-200">
                <a href={businessInfo.phoneLink} className="block">
                  <Button className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold gap-2">
                    <Phone className="w-5 h-5" />
                    Call {businessInfo.phone}
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Map */}
          <Card className="bg-white border-slate-200 overflow-hidden">
            <div className="bg-slate-900 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-slate-900" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Location</h3>
                  <p className="text-slate-400 text-sm">{businessInfo.serviceArea}</p>
                </div>
              </div>
            </div>
            <CardContent className="p-0">
              {/* Google Maps Embed */}
              <div className="aspect-[4/3] w-full">
                <iframe
                  src={businessInfo.mapEmbedUrl}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="DC Lock and Key - South Perth Location"
                ></iframe>
              </div>
              <div className="p-6">
                <a
                  href={businessInfo.directionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold gap-2">
                    <ExternalLink className="w-5 h-5" />
                    Get Directions
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default HoursLocationSection;
